import React, { useState } from "react";
import StudentCard from "./StudentCard";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

const StudentList = ({
  students,
  search,
  sort,
  setSort,
  perPage: perPageProp,
  setPerPage: setPerPageProp,
  onOpen,
  onCheckIn,
  checkingIn,
}) => {
  const [page, setPage] = useState(1);
  const [sortOpen, setSortOpen] = useState(false);
  // Page size can be owned by the parent (the check-in page persists it);
  // fall back to local state when rendered standalone.
  const [localPerPage, setLocalPerPage] = useState(10);
  const perPage = perPageProp ?? localPerPage;
  const setPerPage = setPerPageProp ?? setLocalPerPage;
  const perPageOptions = [5, 10, 15, 30, 50];

  const sortOptions = ["Alphabetical", "Grade", "Age", "Check-In Status"];

  // Reset page when search or sort changes
  React.useEffect(() => {
    setPage(1);
  }, [search, sort, perPage]);

  // SEARCH FILTER
  const filtered = students.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()),
  );

  // SORT
  const sorted = [...filtered].sort((a, b) => {
    switch (sort) {
      case "Alphabetical":
        return a.name.localeCompare(b.name);
      case "Grade":
        return (a.grade || "")
          .toString()
          .localeCompare((b.grade || "").toString());
      case "Age":
        // Sort by DOB ascending (youngest first = latest DOB first)
        if (!a.dob && !b.dob) return 0;
        if (!a.dob) return 1;
        if (!b.dob) return -1;
        return new Date(b.dob) - new Date(a.dob);
      case "Check-In Status":
        return (b.checked ? 1 : 0) - (a.checked ? 1 : 0);
      default:
        return 0;
    }
  });

  // PAGINATION CALCULATION
  const totalPages = Math.ceil(sorted.length / perPage);
  const start = (page - 1) * perPage;
  const currentStudents = sorted.slice(start, start + perPage);

  return (
    <div className="bg-white/60 p-4 lg:p-7 rounded-3xl shadow-md backdrop-blur-md border">
      {/* HEADER */}
      <div className="flex justify-between items-center gap-3 mb-3">
        <h2 className="font-semibold text-lg sm:text-xl font-manrope text-[#1B1B1B] min-w-0 truncate">
          Students ({filtered.filter((s) => s.checked).length}/{filtered.length}
          )
        </h2>

        {/* Sort Dropdown */}
        <div className="relative shrink-0">
          <button
            onClick={() => setSortOpen(!sortOpen)}
            className="flex items-center gap-2 justify-center bg-[#FFFFFF80] text-black w-[120px] sm:w-[150px] h-10 px-3 sm:px-4 py-2 rounded-full shadow text-sm sm:text-[16px]"
          >
            {sort}{" "}
            <ChevronDown
              size={16}
              className={`transition-transform ${sortOpen ? "rotate-180" : ""}`}
            />
          </button>

          {sortOpen && (
            <div className="absolute right-0 mt-1 w-[180px] z-40 bg-white shadow-md border border-gray-200 rounded-xl overflow-hidden">
              {sortOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    setSort(option);
                    setSortOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-100 ${
                    sort === option ? "bg-gray-50 font-semibold" : ""
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* STUDENT CARDS */}
      <div className="flex flex-col gap-2">
        {currentStudents.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4">
            No students found
          </p>
        ) : (
          currentStudents.map((student) => (
            <StudentCard
              key={student.id}
              student={student}
              onOpenModal={onOpen}
              onCheckIn={onCheckIn}
              checkingIn={checkingIn}
            />
          ))
        )}
      </div>

      {/* PER-PAGE SELECTOR + PAGINATION */}
      <div className="flex justify-end items-center gap-2 mt-4">
        <label className="text-sm text-gray-500 font-manrope">Per page:</label>
        <select
          value={perPage}
          onChange={(e) => setPerPage(Number(e.target.value))}
          className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm font-manrope"
        >
          {perPageOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-2">
          {/* PREV (Chevron Left) */}
          <button
            onClick={() => page > 1 && setPage(page - 1)}
            disabled={page === 1}
            className={`p-2 rounded-lg ${
              page === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-[#1B1B1B] hover:bg-gray-100"
            }`}
          >
            <ChevronLeft size={16} />
          </button>

          {/* PAGE NUMBERS */}
          <div className="flex gap-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`px-3 py-1 rounded-lg text-sm ${
                  page === i + 1
                    ? "bg-[#1D3557] text-white"
                    : "text-[#1B1B1B] hover:bg-gray-100"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          {/* NEXT (Chevron Right) */}
          <button
            onClick={() => page < totalPages && setPage(page + 1)}
            disabled={page === totalPages}
            className={`p-2 rounded-lg ${
              page === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-[#1B1B1B] hover:bg-gray-100"
            }`}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default StudentList;
